import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getErrorMessage,
  getOfflinePaymentCollectors,
  getOrderPaymentsLedger,
  postOfflinePayment,
  uploadAdminPaymentProofFile,
} from '../Config/adminOrderFlowService';
import {
  centsToDollarInputString,
  dollarsStringToCents,
  formatCentsAsCurrency,
} from '../Utils/money';

const PAYMENT_MODES = ['CASH', 'EPAY', 'CHECK'];

function inputClass() {
  return 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-100 disabled:text-gray-500';
}

function ledgerData(body) {
  return body?.data ?? body ?? null;
}

export default function OfflinePaymentForm({
  orderId,
  currency = 'CAD',
  onCancel,
  onRecorded,
  onLedgerRefresh,
}) {
  const [amountDollars, setAmountDollars] = useState('');
  const [mode, setMode] = useState('CASH');
  const [collectorId, setCollectorId] = useState('');
  const [collectors, setCollectors] = useState([]);
  const [externalReference, setExternalReference] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofUrl, setProofUrl] = useState('');
  const [note, setNote] = useState('');
  const [remainingCents, setRemainingCents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [retrySubmission, setRetrySubmission] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError('');
      try {
        const [ledgerResponse, collectorsResponse] = await Promise.all([
          getOrderPaymentsLedger(orderId),
          getOfflinePaymentCollectors(orderId),
        ]);
        if (cancelled) return;

        if (!ledgerResponse.ok || ledgerResponse.body?.success === false) {
          throw new Error(getErrorMessage(ledgerResponse.body, 'Failed to load payment balance'));
        }
        if (!collectorsResponse.ok || collectorsResponse.body?.success === false) {
          throw new Error(getErrorMessage(collectorsResponse.body, 'Failed to load payment collectors'));
        }

        const ledger = ledgerData(ledgerResponse.body);
        const nextRemaining = Number(ledger?.summary?.amount_remaining_cents);
        const collectorList = collectorsResponse.body?.data?.collectors;
        setRemainingCents(Number.isFinite(nextRemaining) ? Math.max(0, Math.round(nextRemaining)) : 0);
        setAmountDollars(centsToDollarInputString(Number.isFinite(nextRemaining) ? nextRemaining : 0));
        setCollectors(Array.isArray(collectorList) ? collectorList : []);
      } catch (error) {
        if (!cancelled) setLoadError(error?.message || 'Failed to load offline payment form');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const refreshLedger = async () => {
    const response = await getOrderPaymentsLedger(orderId);
    if (!response.ok || response.body?.success === false) return null;
    const ledger = ledgerData(response.body);
    const nextRemaining = Number(ledger?.summary?.amount_remaining_cents);
    if (Number.isFinite(nextRemaining)) {
      setRemainingCents(Math.max(0, Math.round(nextRemaining)));
    }
    if (onLedgerRefresh) await onLedgerRefresh(ledger);
    return ledger;
  };

  const sendSubmission = async (submission) => {
    const response = await postOfflinePayment(orderId, submission.payload);

    if (response.ok && response.body?.success !== false) {
      setRetrySubmission(null);
      const refreshedLedger = await refreshLedger();
      toast.success(response.body?.message || 'Offline payment recorded');
      if (onRecorded) await onRecorded(refreshedLedger, response.body?.data ?? response.body);
      return;
    }

    if (response.status === 409) {
      await refreshLedger();
      toast.warning(
        'This payment request may already have been recorded. Review the refreshed ledger before retrying.'
      );
      return;
    }

    if (response.status >= 400 && response.status < 500) {
      setRetrySubmission(null);
    }
    toast.error(getErrorMessage(response.body, 'Offline payment failed'));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || loading) return;

    setSubmitting(true);
    try {
      if (retrySubmission) {
        await sendSubmission(retrySubmission);
        return;
      }

      const [ledgerResponse, collectorsResponse] = await Promise.all([
        getOrderPaymentsLedger(orderId),
        getOfflinePaymentCollectors(orderId),
      ]);
      if (!ledgerResponse.ok || ledgerResponse.body?.success === false) {
        throw new Error(getErrorMessage(ledgerResponse.body, 'Failed to refresh payment balance'));
      }
      if (!collectorsResponse.ok || collectorsResponse.body?.success === false) {
        throw new Error(getErrorMessage(collectorsResponse.body, 'Failed to refresh payment collectors'));
      }

      const latestLedger = ledgerData(ledgerResponse.body);
      const latestRemaining = Number(latestLedger?.summary?.amount_remaining_cents);
      const latestCollectors = collectorsResponse.body?.data?.collectors;
      const collectorList = Array.isArray(latestCollectors) ? latestCollectors : [];
      const amountCents = dollarsStringToCents(amountDollars);

      setCollectors(collectorList);
      setRemainingCents(Number.isFinite(latestRemaining) ? Math.max(0, Math.round(latestRemaining)) : 0);

      if (!Number.isInteger(amountCents) || amountCents <= 0) {
        throw new Error('Amount must be greater than zero');
      }
      if (!Number.isFinite(latestRemaining) || amountCents > Math.round(latestRemaining)) {
        throw new Error('Amount cannot exceed the latest remaining invoice balance');
      }
      if (!collectorList.some((collector) => String(collector.user_id) === String(collectorId))) {
        throw new Error('Select an eligible payment collector');
      }
      if (note.length > 2000) {
        throw new Error('Note cannot exceed 2,000 characters');
      }

      let uploadedProofUrl = proofUrl;
      if (proofFile && !uploadedProofUrl) {
        uploadedProofUrl = await uploadAdminPaymentProofFile(proofFile);
        setProofUrl(uploadedProofUrl);
      }
      if (uploadedProofUrl && !uploadedProofUrl.toLowerCase().startsWith('https://')) {
        throw new Error('Payment proof must use a valid HTTPS URL');
      }
      if (!window.crypto?.randomUUID) {
        throw new Error('This browser cannot generate a secure payment request ID');
      }

      const payload = {
        amount_cents: amountCents,
        mode,
        idempotency_key: window.crypto.randomUUID(),
        collected_by_user_id: collectorId,
      };
      if (externalReference.trim()) payload.external_reference = externalReference.trim();
      if (uploadedProofUrl) payload.proof_url = uploadedProofUrl;
      if (note.trim()) payload.note = note.trim();

      const submission = { payload };
      setRetrySubmission(submission);
      await sendSubmission(submission);
    } catch (error) {
      toast.error(error?.message || 'Offline payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const lockedForRetry = Boolean(retrySubmission);
  const noCollectors = !loading && collectors.length === 0;
  const fullyPaid = remainingCents === 0;

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-xl border border-teal-200 bg-teal-50/50 p-4 text-left"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-teal-950">Record offline payment</h3>
          <p className="mt-1 text-xs text-teal-900/75">
            Latest remaining balance:{' '}
            {remainingCents == null ? '—' : formatCentsAsCurrency(remainingCents, currency)}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="text-xs font-medium text-gray-600 underline hover:text-gray-900 disabled:opacity-50"
        >
          Close
        </button>
      </div>

      {loadError ? <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{loadError}</p> : null}
      {noCollectors ? (
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          No eligible payment collector is available for this order.
        </p>
      ) : null}
      {lockedForRetry ? (
        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
          Retrying will resend the exact same payment details and idempotency key. Review the ledger before
          retrying if the previous result was unclear.
        </p>
      ) : null}

      <fieldset disabled={loading || submitting || lockedForRetry} className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Amount</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amountDollars}
            onChange={(event) => setAmountDollars(event.target.value)}
            className={inputClass()}
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Payment mode</label>
          <select value={mode} onChange={(event) => setMode(event.target.value)} className={inputClass()}>
            {PAYMENT_MODES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Collected by</label>
          <select
            value={collectorId}
            onChange={(event) => setCollectorId(event.target.value)}
            className={inputClass()}
            required
          >
            <option value="">Select a collector</option>
            {collectors.map((collector) => (
              <option key={collector.user_id} value={collector.user_id}>
                {collector.name} — {collector.role}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">External reference</label>
          <input
            value={externalReference}
            onChange={(event) => setExternalReference(event.target.value)}
            className={inputClass()}
            placeholder="Receipt, e-payment, or check number"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Proof upload</label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(event) => {
              setProofFile(event.target.files?.[0] || null);
              setProofUrl('');
            }}
            className={inputClass()}
          />
          <p className="mt-1 text-xs text-gray-500">
            Optional image or PDF, max 10 MB; only its HTTPS URL is saved. iPhone (HEIC) photos are
            converted to JPG automatically.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Note</label>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={2000}
            rows={3}
            className={inputClass()}
          />
          <p className="mt-1 text-right text-xs text-gray-500">{note.length}/2,000</p>
        </div>
      </fieldset>

      <button
        type="submit"
        disabled={loading || submitting || Boolean(loadError) || noCollectors || fullyPaid}
        className="rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : lockedForRetry ? 'Retry same payment' : 'Record payment'}
      </button>
    </form>
  );
}
